import { useState } from 'react';
import {
  Search, X, Clock, BarChart3, Zap, ChevronRight,
  Mail, Users, ShoppingCart, Headphones, Share2, Star,
  RefreshCw, GraduationCap, Calendar, DollarSign, Settings,
  Megaphone, Sparkles, Download, Eye, Filter,
} from 'lucide-react';
import { workflowTemplates, TEMPLATE_CATEGORIES, DIFFICULTY_LEVELS, importTemplate } from '../data/workflowTemplates';

import { useTranslation } from 'react-i18next';
const ICON_MAP = {
  Mail, Users, ShoppingCart, Headphones, Share2, BarChart3,
  Star, RefreshCw, GraduationCap, Calendar, DollarSign, Settings,
  Megaphone, Sparkles, Download, Eye, Zap, Timer: Clock,
};

function GetIcon({ name, className = 'w-4 h-4' }) {
  const Icon = ICON_MAP[name] || Zap;
  return <Icon className={className} />;
}

export default function TemplateLibrary({ onImport, onClose }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const categories = ['All', ...Object.keys(TEMPLATE_CATEGORIES)];

  const filtered = workflowTemplates.filter(t => {
    const matchesSearch = !search ||
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleImport = (templateId) => {
    const imported = importTemplate(templateId);
    if (imported && onImport) {
      onImport(imported);
    }
  };

  return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label={t('template_library.template_library')}>
      <div className="w-full max-w-5xl max-h-[85vh] bg-[#09090B] border border-zinc-800 flex flex-col overflow-hidden" tabIndex={-1}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-1" aria-hidden="true">// TEMPLATE LIBRARY</div>
            <h2 className="font-display text-xl uppercase" id="template-library-heading">{t('template_library.workflow_templates')}</h2>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-500 hover:text-white focus-visible:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-sm min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors" aria-label={t('template_library.close_template_library')}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search + Filters */}
        <div className="px-6 py-3 border-b border-zinc-800 space-y-3">
          <form role="search" aria-label={t('template_library.search_templates')} className="relative">
            <label htmlFor="template-search" className="sr-only">{t('template_library.search_templates')}</label>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" aria-hidden="true" />
            <input
              id="template-search"
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('template_library.search_templates')}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white pl-10 pr-4 py-2 font-mono text-xs outline-none transition-colors"
            />
          </form>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-zinc-500" />
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1 font-mono text-[10px] uppercase tracking-wider border transition-colors ${
                  selectedCategory === cat
                    ? 'border-amber-500/50 bg-amber-500/10 text-amber-400'
                    : 'border-zinc-800 text-zinc-500 hover:border-zinc-700 hover:text-zinc-300'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Template Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
              <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider">{t('template_library.no_templates_found')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filtered.map(tpl => {
                const catInfo = TEMPLATE_CATEGORIES[tpl.category] || {};
                const diffInfo = DIFFICULTY_LEVELS[tpl.difficulty] || {};
                return (
                  <div
                    key={tpl.id}
                    className="border border-zinc-800 hover:border-zinc-700 bg-zinc-950/50 p-4 transition-colors group"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded border ${catInfo.color === 'amber' ? 'border-amber-500/30 bg-amber-500/10 text-amber-400' : catInfo.color === 'emerald' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' : catInfo.color === 'sky' ? 'border-sky-500/30 bg-sky-500/10 text-sky-400' : 'border-violet-500/30 bg-violet-500/10 text-violet-400'} flex items-center justify-center`}>
                          <GetIcon name={tpl.icon} className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="font-mono text-xs text-white font-bold">{tpl.name}</h3>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`font-mono text-[9px] uppercase tracking-wider ${catInfo.color === 'amber' ? 'text-amber-400' : catInfo.color === 'emerald' ? 'text-emerald-400' : catInfo.color === 'sky' ? 'text-sky-400' : 'text-violet-400'}`}>
                              {tpl.category}
                            </span>
                            <span className={`font-mono text-[9px] uppercase tracking-wider ${diffInfo.color === 'emerald' ? 'text-emerald-400' : diffInfo.color === 'amber' ? 'text-amber-400' : 'text-red-400'}`}>
                              {tpl.difficulty}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <p className="font-mono text-[10px] text-zinc-500 mb-3 line-clamp-2">{tpl.description}</p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 text-zinc-500">
                          <Clock className="w-3 h-3" />
                          <span className="font-mono text-[9px]">{tpl.estimatedTime}</span>
                        </div>
                        <div className="flex items-center gap-1 text-zinc-500">
                          <BarChart3 className="w-3 h-3" />
                          <span className="font-mono text-[9px]">{tpl.nodes.length} nodes</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setPreviewTemplate(tpl)}
                          className="p-1.5 text-zinc-500 hover:text-white focus-visible:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-sm transition-colors"
                          aria-label={`Preview ${tpl.name}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleImport(tpl.id)}
                          className="flex items-center gap-1 px-2.5 py-1 bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-wider text-[9px] transition-colors"
                        >
                          <Download className="w-3 h-3" /> Import
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Preview Modal */}
        {previewTemplate && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-10">
            <div className="w-full max-w-2xl max-h-[70vh] bg-[#09090B] border border-zinc-800 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
                <div className="flex items-center gap-2">
                  <GetIcon name={previewTemplate.icon} className="w-4 h-4 text-amber-400" />
                  <span className="font-mono text-xs text-white font-bold">{previewTemplate.name}</span>
                </div>
                <button onClick={() => setPreviewTemplate(null)} className="p-1 text-zinc-500 hover:text-white focus-visible:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-sm min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors" aria-label={t('template_library.close_preview')}>
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <p className="font-mono text-xs text-zinc-400">{previewTemplate.description}</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1 text-zinc-500">
                    <Clock className="w-3 h-3" />
                    <span className="font-mono text-[10px]">{previewTemplate.estimatedTime}</span>
                  </div>
                  <div className="flex items-center gap-1 text-zinc-500">
                    <BarChart3 className="w-3 h-3" />
                    <span className="font-mono text-[10px]">{previewTemplate.nodes.length} nodes</span>
                  </div>
                </div>

                {/* Flow preview */}
                <div className="border border-zinc-800 p-3 bg-zinc-950/50">
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Flow Preview</div>
                  <div className="flex items-center gap-1 overflow-x-auto pb-2">
                    {previewTemplate.nodes.map((node, i) => (
                      <div key={i} className="flex items-center shrink-0">
                        <div className={`px-2 py-1 border font-mono text-[8px] uppercase tracking-wider ${
                          node.type === 'trigger' ? 'border-amber-500/30 bg-amber-500/10 text-amber-400' :
                          node.type === 'action' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' :
                          node.type === 'condition' ? 'border-sky-500/30 bg-sky-500/10 text-sky-400' :
                          node.type === 'logic' ? 'border-violet-500/30 bg-violet-500/10 text-violet-400' :
                          node.type === 'ai' ? 'border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-400' :
                          'border-red-500/30 bg-red-500/10 text-red-400'
                        }`}>
                          {node.label}
                        </div>
                        {i < previewTemplate.nodes.length - 1 && (
                          <ChevronRight className="w-3 h-3 text-zinc-600 mx-0.5 shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 border-t border-zinc-800 flex justify-end gap-2">
                <button
                  onClick={() => setPreviewTemplate(null)}
                  className="px-3 py-1.5 border border-zinc-800 text-zinc-400 hover:text-white font-mono text-[10px] uppercase tracking-wider transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => { handleImport(previewTemplate.id); setPreviewTemplate(null); }}
                  className="flex items-center gap-1 px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-wider text-[10px] transition-colors"
                >
                  <Download className="w-3 h-3" /> Import Template
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
